// ============================================================================
// Contracts — approval panel. Role-aware Submit / Approve / Reject controls.
// Reads the role from useAuth() to decide which action(s) are available, but
// the RPCs ultimately enforce permissions in the DB.
// ============================================================================

import { canGiveExecutiveContractApproval, canSendContracts, isSuperuser } from '../../shared/role-gates';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, ErrorState } from '../../shared/components';
import {
  approveExecutive,
  approveFinance,
  approveLegal,
  markContractStatus,
  rejectContract,
  submitForFinance,
} from './hooks/use-contract-data';
import type { ContractLineItemRow, ContractRow } from './types';
import { errorMessage } from '../../shared/errors';

interface ApprovalPanelProps {
  contract: ContractRow;
  lineItems: ContractLineItemRow[];
  role: string | null;
  // Finance-capable: primary finance/exec/superuser OR extra_roles 'finance'
  // hat. Computed by the parent via hasFinanceCapability(profile) so the
  // Pending_Finance approve/reject controls mirror the contracts_update_finance
  // RLS (now on has_finance_capability()).
  financeCapable: boolean;
  currentEid: string | null;
  onAfterAction: () => void;
}

function isMissing(
  contract: ContractRow,
  lineItems: ContractLineItemRow[],
): string[] {
  const missing: string[] = [];
  if (!contract.account_id) missing.push('Account');
  if (!contract.contract_type) missing.push('Contract type');
  if (!contract.billing_model) missing.push('Billing model');
  if (!contract.start_date) missing.push('Start date');
  if (!contract.scope_summary || !contract.scope_summary.trim()) {
    missing.push('Scope summary');
  }
  if (contract.billing_model === 'Monthly' || contract.billing_model === 'Hybrid') {
    if (contract.monthly_rate == null) missing.push('Monthly rate');
  }
  if (contract.billing_model === 'Hourly' || contract.billing_model === 'Hybrid') {
    if (contract.hourly_rate == null) missing.push('Hourly rate');
    if (contract.estimated_monthly_hours == null) {
      missing.push('Estimated monthly hours');
    }
  }
  if (lineItems.length === 0) missing.push('At least one line item');
  return missing;
}

export function ApprovalPanel({
  contract,
  lineItems,
  role,
  financeCapable,
  currentEid,
  onAfterAction,
}: ApprovalPanelProps) {
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const isCreator = contract.created_by_eid === currentEid;
  const isSuper = isSuperuser(role);
  // Finance approval is now capability-driven (mirrors contracts_update_finance
  // RLS). has_finance_capability() already covers finance/exec/superuser plus
  // extra_roles 'finance' holders, so this is a superset of the old role check.
  const isFinance = financeCapable;
  const isExec = canGiveExecutiveContractApproval(role);

  const missing = isMissing(contract, lineItems);
  const canSubmit =
    contract.status === 'Draft' && (isCreator || isSuper) && missing.length === 0;

  const canApproveFinance =
    contract.status === 'Pending_Finance' && (isFinance || isSuper);
  const canApproveLegal =
    contract.status === 'Pending_Legal' && isExec;
  const canApproveExec =
    contract.status === 'Pending_Executive' && isExec;
  const canReject =
    (contract.status === 'Pending_Finance' && (isFinance || isSuper)) ||
    (contract.status === 'Pending_Legal' && isExec) ||
    (contract.status === 'Pending_Executive' && isExec);

  const canMarkSent =
    contract.status === 'Ready_for_Signature' && (isCreator || canSendContracts(role));
  const canMarkSigned =
    contract.status === 'Sent_for_Signature' && (isCreator || canSendContracts(role));
  const canMarkActive =
    contract.status === 'Signed' && (isCreator || canSendContracts(role));

  async function runAction(name: string, fn: () => Promise<void>) {
    setActionBusy(name);
    setActionError(null);
    try {
      await fn();
      setActionNotes('');
      setRejectReason('');
      onAfterAction();
    } catch (err) {
      setActionError(errorMessage(err, `Failed to ${name}.`));
    } finally {
      setActionBusy(null);
    }
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <h3 className="mb-3 text-sm font-semibold text-tcs-text">Actions</h3>

      {actionError && (
        <div className="mb-3">
          <ErrorState title="Action failed" body={actionError} />
        </div>
      )}

      {/* Submit for finance (Draft → Pending_Finance) */}
      {contract.status === 'Draft' && (isCreator || isSuper) && (
        <div className="space-y-2">
          {missing.length > 0 ? (
            <div className="rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
              <div className="font-semibold">
                Required before submitting:
              </div>
              <ul className="ml-4 list-disc">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-xs text-tcs-text-secondary">
              Ready to submit for finance review.
            </div>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!canSubmit || actionBusy != null}
            onClick={() =>
              runAction('submit', () => submitForFinance(contract.contract_id))
            }
          >
            {actionBusy === 'submit' && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit for Finance Review
          </Button>
        </div>
      )}

      {/* Approval stage controls */}
      {(canApproveFinance || canApproveLegal || canApproveExec || canReject) && (
        <div className="space-y-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
              Approval notes (optional)
            </span>
            <textarea
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            {canApproveFinance && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={actionBusy != null}
                onClick={() =>
                  runAction('approve_finance', () =>
                    approveFinance(contract.contract_id, actionNotes || null),
                  )
                }
              >
                {actionBusy === 'approve_finance' && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Approve (Finance)
              </Button>
            )}
            {canApproveLegal && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={actionBusy != null}
                onClick={() =>
                  runAction('approve_legal', () =>
                    approveLegal(contract.contract_id, actionNotes || null),
                  )
                }
              >
                {actionBusy === 'approve_legal' && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Approve (Legal)
              </Button>
            )}
            {canApproveExec && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={actionBusy != null}
                onClick={() =>
                  runAction('approve_exec', () =>
                    approveExecutive(contract.contract_id, actionNotes || null),
                  )
                }
              >
                {actionBusy === 'approve_exec' && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Approve (Executive)
              </Button>
            )}
          </div>

          {canReject && (
            <div className="mt-3 space-y-2 rounded-md border border-st-danger-border bg-st-danger-bg/60 p-3">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-st-danger-fg-strong">
                  Rejection reason (required)
                </span>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={2}
                  className="block w-full rounded-md border border-st-danger-border-strong bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-st-danger-border-strong focus:outline-none focus:ring-1 focus:ring-st-danger-border-strong"
                />
              </label>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={actionBusy != null || !rejectReason.trim()}
                onClick={() =>
                  runAction('reject', () =>
                    rejectContract(contract.contract_id, rejectReason.trim()),
                  )
                }
              >
                {actionBusy === 'reject' && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Reject contract
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Post-approval transitions */}
      {(canMarkSent || canMarkSigned || canMarkActive) && (
        <div className="space-y-2">
          {canMarkSent && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={actionBusy != null}
              onClick={() =>
                runAction('mark_sent', () =>
                  markContractStatus(contract.contract_id, 'Sent_for_Signature'),
                )
              }
            >
              {actionBusy === 'mark_sent' && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Mark as Sent for Signature
            </Button>
          )}
          {canMarkSigned && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={actionBusy != null}
              onClick={() =>
                runAction('mark_signed', () =>
                  markContractStatus(contract.contract_id, 'Signed'),
                )
              }
            >
              {actionBusy === 'mark_signed' && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Mark as Signed
            </Button>
          )}
          {canMarkActive && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={actionBusy != null}
              onClick={() =>
                runAction('mark_active', () =>
                  markContractStatus(contract.contract_id, 'Active'),
                )
              }
            >
              {actionBusy === 'mark_active' && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Mark as Active
            </Button>
          )}
        </div>
      )}

      {/* Final / terminal states */}
      {(contract.status === 'Active' ||
        contract.status === 'Expired' ||
        contract.status === 'Cancelled') && (
        <div className="text-xs text-tcs-text-secondary">
          Contract is in a terminal state. No further actions.
        </div>
      )}

      {contract.status === 'Rejected' && (
        <div className="rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong">
          <div className="font-semibold">Rejected.</div>
          {contract.rejection_reason && <div className="mt-1">{contract.rejection_reason}</div>}
        </div>
      )}
    </div>
  );
}
